import { btnUtils } from "./utils";
import type { FocusableElements } from "./types/modals";

/* Modal Functionality */
let modalInitiatingElement: HTMLElement | null = null;

/**
 * Opens a modal with the specified ID and sets up focus management for accessibility.
 * @param {string} modalId - The ID of the modal to open.
 * @param {HTMLElement} [initiatingElement] - The element that initiated the modal opening, used to return focus when the modal is closed.
 */
export const recModalOpen = (modalId: string, initiatingElement?: HTMLElement) => {
  const body = document.querySelector("body") as HTMLBodyElement;
  const modal = document.getElementById(modalId);
  const focusableElements =
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
  if (modal && modal.querySelector(".rec-modal-body")) {
    recModalsClose();

    if (!modal.classList.contains("rec-modal-active")) {
      const focusableContent: FocusableElements = modal.querySelectorAll(
        focusableElements,
      ) as FocusableElements;
      const firstFocusableElement = focusableContent[0];
      const lastFocusableElement =
        focusableContent[focusableContent.length - 1];

      document.addEventListener("keydown", function (e) {
        const isTabPressed = e.key === "Tab";

        if (!isTabPressed) {
          return;
        }

        if (e.shiftKey) {
          // if shift key pressed for shift + tab combination
          if (document.activeElement === firstFocusableElement) {
            lastFocusableElement.focus(); // add focus for the last focusable element
            e.preventDefault();
          }
        } else {
          // if tab key is pressed
          if (document.activeElement === lastFocusableElement) {
            // if focused has reached to last focusable element then focus first focusable element after pressing tab
            firstFocusableElement.focus(); // add focus for the first focusable element
            e.preventDefault();
          }
        }
      });

      if (firstFocusableElement) {
        firstFocusableElement.focus();
      }
      modal.classList.add("rec-modal-active");
    }

    modal.classList.remove("rec-opacity-0");
    modal.classList.remove("rec-hidden");
    modal.classList.remove("rec-pointer-events-none");
    body.classList.add("rec-modal-active");
    if (initiatingElement) {
      modalInitiatingElement = initiatingElement;
    }
    modal.focus();
  } else {
    recModalsClose();
  }
};

/**
 * Closes all open modals and resets the focus to the initiating element.
 */
export const recModalsClose = () => {
  const body = document.querySelector("body") as HTMLBodyElement;
  const recModals = document.querySelectorAll(".rec-modal");
  for (let i = 0; i < recModals.length; i++) {
    recModals[i].classList.add("rec-opacity-0");
    recModals[i].classList.add("rec-hidden");
    recModals[i].classList.add("rec-pointer-events-none");
    body.classList.remove("rec-modal-active");
  }
  btnUtils.reset();
  if (modalInitiatingElement) {
    modalInitiatingElement.focus();
    modalInitiatingElement = null;
  }
};

/**
 * Initializes the close functionality for all elements with the class "rec-modal-close".
 * Adds a click event listener to each element to close all open modals when clicked.
 */
export const recModalsCloseInit = () => {
  document.querySelectorAll(".rec-modal-close").forEach((thisModalClose) => {
    thisModalClose.addEventListener("click", (e) => {
      e.preventDefault();
      recModalsClose();
    });
  });
};

/**
 * Initializes modal functionality, including opening and closing modals.
 * - Sets up a keydown event listener to close modals when the Escape key is pressed.
 * - Adds click event listeners to elements with the class "rec-modal-open" to open the specified modal.
 * - Initializes the close functionality for modal close elements.
 */
export const modalsInit = () => {
  document.onkeydown = function (evt: KeyboardEvent) {
    let isEscape = false;
    if ("key" in evt) {
      isEscape = evt.key === "Escape" || evt.key === "Esc";
    }
    if (
      isEscape &&
      document.body &&
      document.body.classList.contains("rec-modal-active")
    ) {
      recModalsClose();
    }
  };
  /* modal openings and closures */
  document.querySelectorAll(".rec-modal-open").forEach((thisModalOpen) => {
    thisModalOpen.addEventListener("click", (e: Event) => {
      e.preventDefault();
      const target = e.target as HTMLAnchorElement | null;
      const currentTarget = e.currentTarget as HTMLAnchorElement | null;
      if (target && currentTarget) {
        const modalId = target.getAttribute("data-target");
        if (modalId) {
          recModalOpen(modalId, currentTarget);
        }
      }
    });
  });
  recModalsCloseInit();
};
